import { PRODUCT_TYPE_IDS } from "./constants";

interface LocalCartSummaryItemLike {
  quantity?: number | null;
  product?: { productType?: { id?: number | null } | null } | null;
}

export interface LocalCartAmounts {
  amountBottles: number;
  amountPresents: number;
}

// Product types the cart tray never lists (subscription plans, packaging, inserts).
// Mirrors filterInvalidProductTypesToDisplay in checkNonWineProductType.
const HIDDEN_PRODUCT_TYPE_IDS = new Set<number>([
  PRODUCT_TYPE_IDS.DB_ID_PRODUCT_TYPE_SUBSCRIPTION_PRE_PAID,
  PRODUCT_TYPE_IDS.DB_ID_PRODUCT_TYPE_SUBSCRIPTION,
  PRODUCT_TYPE_IDS.DB_ID_PRODUCT_TYPE_BOX,
  PRODUCT_TYPE_IDS.DB_ID_PRODUCT_TYPE_BOX_INSERT,
  PRODUCT_TYPE_IDS.DB_ID_PRODUCT_TYPE_FLYER,
]);

// What the backend's ShoppingCartObject.amount_presents counts.
const PRESENT_PRODUCT_TYPE_IDS = new Set<number>([
  PRODUCT_TYPE_IDS.DB_ID_PRODUCT_TYPE_GIFT,
  PRODUCT_TYPE_IDS.DB_ID_PRODUCT_TYPE_SPECIAL_PACK,
  PRODUCT_TYPE_IDS.DB_ID_PRODUCT_TYPE_SHIPPING_PRE_PAID,
  PRODUCT_TYPE_IDS.DB_ID_PRODUCT_TYPE_FREEBIE,
]);

/**
 * Whether a guest's local cart line is shown in the cart tray. Any product type the tray lists
 * (wines, beers, merch, gifts, special packs...) keeps the tray mounted for guests.
 */
export const isDisplayableLocalCartItem = (item: LocalCartSummaryItemLike | null | undefined): boolean => {
  const productTypeId = item?.product?.productType?.id;
  return typeof productTypeId === "number" && !HIDDEN_PRODUCT_TYPE_IDS.has(productTypeId);
};

/**
 * Bottle and present counts for a guest's local cart, mirroring the member cart's server-side
 * `amountBottles` / `amountPresents` (base `ShoppingCartObject.resolve_amount_bottles` /
 * `resolve_amount_presents`): gifts, special packs, pre-paid shipping and rewards are presents;
 * every other listed product (wines and non-wine products such as beers) counts as a bottle.
 */
export const countLocalCartAmounts = (
  items: ReadonlyArray<LocalCartSummaryItemLike | null | undefined> | null | undefined
): LocalCartAmounts =>
  (items ?? []).reduce<LocalCartAmounts>(
    (amounts, item) => {
      if (!isDisplayableLocalCartItem(item)) return amounts;
      const quantity = Number(item?.quantity) || 0;
      const productTypeId = item?.product?.productType?.id as number;
      return PRESENT_PRODUCT_TYPE_IDS.has(productTypeId)
        ? { ...amounts, amountPresents: amounts.amountPresents + quantity }
        : { ...amounts, amountBottles: amounts.amountBottles + quantity };
    },
    { amountBottles: 0, amountPresents: 0 }
  );
